import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dictionary, isEqual } from 'lodash';
import { DeviceTypeFilter } from './filters/DeviceTypeFilter';
import { DeviceStatusFilter } from './filters/DeviceStatusFilter';
import { getSelectedKeys } from 'logic/getSelectedKeys';

require('./DeviceTreeFilter.css');

export type Props = {
    selectedDeviceTypes: Dictionary<boolean>;
    selectedDeviceStatuses: Dictionary<boolean>;
    onChange(
        fnTypes: Dictionary<boolean>,
        fnStatuses: Dictionary<boolean>,
    ): void;
};

export const DeviceTreeFilter = ({
    selectedDeviceTypes,
    selectedDeviceStatuses,
    onChange,
}: Props) => {
    const { t } = useTranslation();

    const [ localSelectedDeviceTypes, setLocalSelectedDeviceTypes ] = useState<Dictionary<boolean>>(selectedDeviceTypes);
    const [ localSelectedDeviceStatuses, setLocalSelectedDeviceStatuses ] = useState<Dictionary<boolean>>(selectedDeviceStatuses);

    const handleFilter = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        onChange(localSelectedDeviceTypes, localSelectedDeviceStatuses);
        e.preventDefault();
        return false;
    }, [ onChange, localSelectedDeviceTypes, localSelectedDeviceStatuses ]);

    const handleClickReset = useCallback(() => {
        setLocalSelectedDeviceTypes(() => ({}));
        setLocalSelectedDeviceStatuses(() => ({}));
        onChange({}, {});
    }, [ setLocalSelectedDeviceTypes, setLocalSelectedDeviceStatuses, onChange ]);

    const canFilter = useMemo(() => {
        const typeIds = getSelectedKeys(selectedDeviceTypes);
        const statusIds = getSelectedKeys(selectedDeviceStatuses);
        const localTypeIds = getSelectedKeys(localSelectedDeviceTypes);
        const localStatusIds = getSelectedKeys(localSelectedDeviceStatuses);
        return !isEqual(typeIds, localTypeIds) || !isEqual(statusIds, localStatusIds);
    }, [ selectedDeviceTypes, selectedDeviceStatuses, localSelectedDeviceTypes, localSelectedDeviceStatuses ]);

    const canReset = useMemo(() => {
        const localTypeIds = getSelectedKeys(localSelectedDeviceTypes);
        const localStatusIds = getSelectedKeys(localSelectedDeviceStatuses);
        return localTypeIds.length > 0 || localStatusIds.length > 0;
    }, [ localSelectedDeviceTypes, localSelectedDeviceStatuses ]);

    return (
        <form
            id="device-tree-filter"
            onSubmit={ handleFilter }
        >
            <h3>
                { t('DEVICES.FILTER') }
            </h3>
            <DeviceTypeFilter
                selectedDeviceTypes={ localSelectedDeviceTypes }
                onChange={ setLocalSelectedDeviceTypes }
            />
            <DeviceStatusFilter
                selectedDeviceStatuses={ localSelectedDeviceStatuses }
                onChange={ setLocalSelectedDeviceStatuses }
            />
            <div>
                <button
                    type="submit"
                    className="ui tiny red button"
                    disabled={ !canFilter }
                >
                    { t('COMMAND.FILTER') }
                </button>
                <button
                    type="button"
                    className="ui tiny button"
                    disabled={ !canReset }
                    onClick={ handleClickReset }
                >
                    { t('COMMAND.RESET') }
                </button>
            </div>
        </form>
    );
};
